import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { AccountData } from '../../api';
import { useAuth } from '../../session/InternalAuthProvider';
import { DATE_FORMAT } from '../consts';
import { AgendaStatusFilter } from './utils/occupancyMath';

/**
 * "עכשיו" mode replaces "היום" in the areas view: the selected time follows the account clock until
 * the admin picks a time, which PINS it. The view never snaps back on its own.
 */
export enum AreasTimeMode {
  LIVE = 'live',
  PINNED = 'pinned',
}

export enum AreasPinSource {
  SCROLL = 'scroll',
  EXPLICIT = 'explicit',
}

export type AreasTimeState =
  | { type: AreasTimeMode.LIVE }
  | { type: AreasTimeMode.PINNED; selectedSeconds: number; source: AreasPinSource };

export type AreasScrollRequest = { seconds: number; token: number; at: number; toNow: boolean };

export type AgendaHighlightRequest = { itemId: string; date: string; seconds: number; token: number; at: number };

/** A highlight request older than this is stale (the item never arrived, or the user moved on). */
export const HIGHLIGHT_REQUEST_VALIDITY_MS = 10000;

type AreasViewContextType = {
  timeState: AreasTimeState;
  /** Seconds from midnight currently being shown, whichever mode we are in. */
  selectedSeconds: number;
  isLive: boolean;
  pinTime: (seconds: number, source?: AreasPinSource) => void;
  /** No-op if already LIVE. EXPLICIT (a control) bumps lastExplicitAt and re-syncs the clock; SCROLL (the list) only
      flips the state. */
  enterLiveMode: (source?: AreasPinSource) => void;
  /** performance.now() of the last EXPLICIT pin or enterLiveMode from a control; the list ignores scroll for 1000 ms
      after it. */
  lastExplicitAt: number;
  /** Set by controls that want the list to scroll. `at` = performance.now(); the list acts on a request for up to 5 s
      (so a request made before the day's orders arrived still lands once they do). */
  scrollRequest: AreasScrollRequest | null;
  /** `toNow` targets the now divider with the freshly synced clock, so a stale closure value never lands a group off. */
  requestScrollTo: (seconds: number, toNow?: boolean) => void;
  /** Today in the account timezone — the only date "now" can be live on. */
  accountToday: string;
  nowSeconds: number;
  isStripExpanded: boolean;
  setIsStripExpanded: (expanded: boolean) => void;
  /** Phone only: the list is scrolled down, so the stats, toolbar and occupancy strip fold away (search and the status
      chips stay). Set by the list's own scroll; reset when the list leaves the phone agenda. */
  isPhoneTopCollapsed: boolean;
  setIsPhoneTopCollapsed: (collapsed: boolean) => void;
  statusFilter: AgendaStatusFilter;
  setStatusFilter: (filter: AgendaStatusFilter) => void;
  /** Area id tapped in the strip; narrows the list to orders holding a table there. Null = every area. */
  areaFilter: string | null;
  setAreaFilter: (areaId: string | null) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  highlightRequest: AgendaHighlightRequest | null;
  /** Scroll to and frame this order or session once its card is in the list; the list clears it after a while or on
      a tap. */
  requestHighlight: (item: { id: string; date?: string | null; time?: number | null }) => void;
  clearHighlight: () => void;
};

const AreasViewContext = createContext<AreasViewContextType | undefined>(undefined);

const LIVE_TICK_MS = 30000;

export const secondsOfDayInTimezone = (timezone: string): number => {
  const now = moment().tz(timezone);
  return now.hours() * 3600 + now.minutes() * 60;
};

export const todayInTimezone = (timezone: string): string => moment().tz(timezone).format(DATE_FORMAT);

export const AreasViewProvider = ({ children }: { children: ReactNode }) => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const timezone = account?.timezone || moment.tz.guess();

  const [timeState, setTimeState] = useState<AreasTimeState>({ type: AreasTimeMode.LIVE });
  const [lastExplicitAt, setLastExplicitAt] = useState(0);
  const [scrollRequest, setScrollRequest] = useState<AreasScrollRequest | null>(null);
  // The strip opens with the squares only; the agenda expands it after an order is created, and
  // the admin can open it by hand.
  const [isStripExpanded, setIsStripExpanded] = useState(false);
  const [isPhoneTopCollapsed, setIsPhoneTopCollapsed] = useState(false);
  const [statusFilter, setStatusFilter] = useState<AgendaStatusFilter>(AgendaStatusFilter.ALL);
  const [areaFilter, setAreaFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightRequest, setHighlightRequest] = useState<AgendaHighlightRequest | null>(null);
  const [nowSeconds, setNowSeconds] = useState(() => secondsOfDayInTimezone(timezone));
  const [accountToday, setAccountToday] = useState(() => todayInTimezone(timezone));

  const syncClock = useCallback(() => {
    setNowSeconds(secondsOfDayInTimezone(timezone));
    setAccountToday(todayInTimezone(timezone));
  }, [timezone]);

  useEffect(() => {
    syncClock();
    const interval = window.setInterval(syncClock, LIVE_TICK_MS);
    window.addEventListener('focus', syncClock);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', syncClock);
    };
  }, [syncClock]);

  const pinTime = useCallback((seconds: number, source: AreasPinSource = AreasPinSource.EXPLICIT) => {
    if (source === AreasPinSource.EXPLICIT) {
      setLastExplicitAt(performance.now());
    }
    setTimeState({ type: AreasTimeMode.PINNED, selectedSeconds: seconds, source });
  }, []);

  const enterLiveMode = useCallback(
    (source: AreasPinSource = AreasPinSource.EXPLICIT) => {
      if (source === AreasPinSource.EXPLICIT) {
        syncClock();
        setLastExplicitAt(performance.now());
      }
      setTimeState((current) => (current.type === AreasTimeMode.LIVE ? current : { type: AreasTimeMode.LIVE }));
    },
    [syncClock],
  );

  const requestScrollTo = useCallback(
    (seconds: number, toNow = false) => {
      setScrollRequest({
        seconds: toNow ? secondsOfDayInTimezone(timezone) : seconds,
        token: Date.now(),
        at: performance.now(),
        toNow,
      });
    },
    [timezone],
  );

  const highlightExpiryRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(highlightExpiryRef.current), []);

  const requestHighlight = useCallback((item: { id: string; date?: string | null; time?: number | null }) => {
    window.clearTimeout(highlightExpiryRef.current);
    highlightExpiryRef.current = window.setTimeout(() => setHighlightRequest(null), HIGHLIGHT_REQUEST_VALIDITY_MS);
    setHighlightRequest({
      itemId: item.id,
      date: item.date ?? '',
      seconds: item.time ?? 0,
      token: Date.now(),
      at: performance.now(),
    });
  }, []);

  const clearHighlight = useCallback(() => setHighlightRequest(null), []);

  const selectedSeconds = timeState.type === AreasTimeMode.LIVE ? nowSeconds : timeState.selectedSeconds;

  const value = useMemo(
    (): AreasViewContextType => ({
      timeState,
      selectedSeconds,
      isLive: timeState.type === AreasTimeMode.LIVE,
      pinTime,
      enterLiveMode,
      lastExplicitAt,
      scrollRequest,
      requestScrollTo,
      accountToday,
      nowSeconds,
      isStripExpanded,
      setIsStripExpanded,
      isPhoneTopCollapsed,
      setIsPhoneTopCollapsed,
      statusFilter,
      setStatusFilter,
      areaFilter,
      setAreaFilter,
      searchQuery,
      setSearchQuery,
      highlightRequest,
      requestHighlight,
      clearHighlight,
    }),
    [
      timeState,
      selectedSeconds,
      pinTime,
      enterLiveMode,
      lastExplicitAt,
      scrollRequest,
      requestScrollTo,
      accountToday,
      nowSeconds,
      isStripExpanded,
      isPhoneTopCollapsed,
      statusFilter,
      areaFilter,
      searchQuery,
      highlightRequest,
      requestHighlight,
      clearHighlight,
    ],
  );

  return <AreasViewContext.Provider value={value}>{children}</AreasViewContext.Provider>;
};

export const useAreasView = (): AreasViewContextType => {
  const context = useContext(AreasViewContext);
  if (!context) {
    throw new Error('useAreasView must be used inside an AreasViewProvider');
  }
  return context;
};

export const useAreasViewOptional = (): AreasViewContextType | undefined => useContext(AreasViewContext);
